import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Shield, Layers, Users, CheckCircle } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const acquisitionPoints = [
  {
    icon: TrendingUp,
    title: "Predictable Demand",
    description: "Systematized lead generation and qualification that operates independent of founder activity.",
  },
  {
    icon: Shield,
    title: "Transferable Authority",
    description: "Documented authority infrastructure that maintains credibility through ownership transitions.",
  },
  {
    icon: Layers,
    title: "Institutional Systems",
    description: "Operational protocols that enable new operators to maintain quality and standards.",
  },
  {
    icon: Users,
    title: "Founder Independence",
    description: "Business value that persists without founder presence or ongoing involvement.",
  },
];

const valueComponents = [
  "Authority Intake Engine with documented qualification criteria",
  "Decision Control Layer with standardized approval logic",
  "Follow-Up Intelligence Engine with automated progression",
  "Operator Handoff Architecture with training protocols",
  "Complete system documentation for due diligence",
  "Governance framework for authority transfer",
];

const BuyerPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="buyer-page">
      <SEOHead 
        title="Buyer"
        description="Transaction Rationale: An institutional authority system generating predictable, transferable demand independent of founder presence."
        url="https://onpointauthority.com/buyer"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12" data-testid="buyer-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Transaction Rationale
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              What Is Being<br />
              <span className="text-primary">Acquired</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              An institutional authority system generating predictable, transferable 
              demand independent of founder presence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="value-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Acquisition Value
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              Institutional-Grade<br />
              <span className="text-primary">Authority Assets</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {acquisitionPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-transparent border border-white/5 p-8 md:p-10 service-card"
                data-testid={`acquisition-point-${index}`}
              >
                <point.icon className="h-8 w-8 text-primary mb-6" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl font-normal text-foreground mb-4">
                  {point.title}
                </h3>
                <p className="text-base font-light leading-relaxed text-muted-foreground">
                  {point.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="included-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                System Components
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-6">
                What's<br />
                <span className="text-primary">Included</span>
              </h2>
              <p className="text-lg font-light leading-relaxed text-muted-foreground">
                A complete authority operating system designed for 
                institutional transfer and operational continuity.
              </p>
            </div>

            <div className="bg-card/5 border border-white/5 p-8">
              <ul className="space-y-4">
                {valueComponents.map((component, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-foreground">{component}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Risk Mitigation */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="risk-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Risk Mitigation
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-8">
              Structural Safeguards
            </h2>
            
            <div className="space-y-6 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                Traditional service businesses carry significant key-person risk. 
                Authority is typically non-transferable—residing entirely in the 
                founder's reputation, relationships, and presence.
              </p>
              <p>
                OnPoint Authority Systems addresses this structural risk through 
                documented, systematized authority infrastructure that maintains 
                institutional credibility independent of any individual.
              </p>
              <p className="text-primary">
                This is not a personality-based business. This is infrastructure.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-8">
              Evaluate Acquisition<br />
              <span className="text-primary">Readiness</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-10">
              Request an authority review to determine system maturity 
              and acquisition readiness.
            </p>
            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="buyer-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BuyerPage;
